import {User} from "../../../../../api/accessControl/User.ts";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Shell from "../../../../../components/specific/Shell.tsx";
import {BackendApi} from "../../../../../api/api.ts";
import {TransactionsTable, TransactionsTableViewerRole} from "../../../entrepreneur/pages/TransactionsPage/TransactionsTable.tsx";
import {MatchedStatusPill} from "../../../entrepreneur/pages/TransactionsPage/TransactionStatusCell.tsx";
import {Transaction} from "../../../entrepreneur/TransactionsTable.tsx";
import {BankAccountData, CustomerBankAccount} from "../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import TransactionFilterBar from "./TransactionFilterBar.tsx";
import {TransactionFilters} from "./partnerTransactions.ts";
import {useSearchParams} from "react-router-dom";
import Modal from "../../../../../components/general/Modal.tsx";
import {MatchDetailsView} from "../../../../../components/matches/MatchDetailsView.tsx";
import {useMatchDetailsByMatchId} from "../../../../../hooks/useMatchDetails.ts";
import {useDocumentDrawer} from "../../../../../views/DocumentDrawer";
import { BookTransactionModal } from "../../../../../components/specific/BookTransactionModal/BookTransactionModal.tsx";
import { UndoBookAwayConfirmationModal } from "./components/UndoBookAwayConfirmationModal.tsx";
import {useQueryClient} from "@tanstack/react-query";
import {QueryKey} from "../../../../../api/queryKeys.ts";
import {usePartnerTransactionsInfinite, type PartnerTransactionItem} from "./usePartnerTransactionsInfinite.ts";
import {usePartnerTransactionPageEnrichment} from "./usePartnerTransactionPageEnrichment.ts";
import {InfiniteScrollFooter} from "./components/InfiniteScrollFooter.tsx";
import {
    columnToSortField,
    DEFAULT_DIRECTION,
    DEFAULT_SORT,
    isSortDirection,
    isSortField,
    SORT_FIELD_TO_COLUMN,
    SORTABLE_COLUMNS,
    toPartnerTransactionsParams,
} from "./partnerTransactionsParams.ts";
import {findScrollParent} from "../../../../../lib/scrollParent.ts";
import type {SortingState} from "../../../../../components/table/CreateTable.tsx";
import { useFilterStore, type FilterState } from "../../../../../contexts/FilterStore.tsx";
import {usePartnerClientScope} from "../../../../../hooks/usePartnerClientScope.ts";
import {ClientSubtitle} from "../../../../../components/specific/ClientSwitcher";
import {PartnerClientToolbar, getVisibleGoToItemIds} from "../../../../../components/specific/PartnerClientToolbar";
import {DEFAULT_GL_ACCOUNT, useGLAccounts} from "../../../../../hooks/useGLAccounts.ts";
import { glAccountSummaryFromApiResponse } from "../../../../../types/GlAccountSummary.ts";
import {useTransactionCategoryChange} from "../../../../../hooks/useTransactionCategoryChange.ts";
import {SubledgerProvider} from "../../../../../contexts/SubledgerContext.tsx";
import {
    TransactionAmountComparatorEnum,
    TransactionFlowFilterEnum,
    TransactionMatchFilterEnum,
} from "../../../../../types/enums.ts";

const transactionByItem = new WeakMap<PartnerTransactionItem, Transaction>();

function ignoreQueryUpdate() {}

function mapPartnerTransaction(item: PartnerTransactionItem): Transaction {
    const cached = transactionByItem.get(item);
    if (cached) {
        return cached;
    }

    const transaction = {
        id: item.transaction_id,
        desc: item.desc,
        bookingDate: item.booking_date,
        amount: item.amount,
        flow: item.flow,
        relationName: item.relation_name,
        relationIBAN: item.relation_iban,
        value: item.flow === 'inflow' ? item.amount : -item.amount,
        accountId: item.account_id,
        generalLedgerAccount: glAccountSummaryFromApiResponse(item.general_ledger_account, item.subledger),
        reconciliationId: item.reconciliation_id ?? null,
        vatCode: item.vat_code ?? null,
        matchingExclusionReason: item.matching_exclusion_reason ?? null,
        mdrStatus: {
            isMdr: item.mdr_status.is_mdr,
            exclusionReason: item.mdr_status.exclusion_reason,
            automationRuleId: item.mdr_status.automation_rule_id,
        },
    } as Transaction;
    transactionByItem.set(item, transaction);
    return transaction;
}

function filtersFromParams(searchParams: URLSearchParams): TransactionFilters {
    const filters: TransactionFilters = {};
    for (const [key, value] of searchParams.entries()) {
        if (key === 'sort' || key === 'direction') {
            continue;
        }
        if (key === 'amount') {
            filters[key] = parseFloat(value);
        } else {
            filters[key] = value as any;
        }
    }
    if (filters.amount != null && !filters.amountComparator) {
        filters.amountComparator = TransactionAmountComparatorEnum.GT;
    }
    return filters;
}

function withStoreDefaults(searchParams: URLSearchParams, filterStore: FilterState): URLSearchParams {
    const nextParams = new URLSearchParams(searchParams);

    const storeClientId = filterStore.common.customerId || '';
    const storeDateFrom = filterStore.common.dateRange.from || '';
    const storeDateTo = filterStore.common.dateRange.to || '';
    const storeFlow = filterStore.transactions.flow;
    const storeAmount = filterStore.transactions.amount;
    const storeAmountComparator = filterStore.transactions.amountComparator;
    const storeBankAccountId = filterStore.transactions.bankAccountId || '';
    const storeRelationIban = filterStore.transactions.relationIban || '';
    const storeGeneralLedgerAccountId = filterStore.transactions.generalLedgerAccountId || '';
    const storeMatchStatus = filterStore.transactions.matchStatus;
    const storeSearch = filterStore.transactions.search || '';

    if (storeClientId && !nextParams.get('clientId')) {
        nextParams.set('clientId', storeClientId);
    }
    if (storeDateFrom && !nextParams.get('dateFrom')) {
        nextParams.set('dateFrom', storeDateFrom);
    }
    if (storeDateTo && !nextParams.get('dateTo')) {
        nextParams.set('dateTo', storeDateTo);
    }
    if (storeFlow && storeFlow !== TransactionFlowFilterEnum.ANY && !nextParams.get('flow')) {
        nextParams.set('flow', storeFlow);
    }
    if (storeAmount != null && !nextParams.get('amount')) {
        nextParams.set('amount', String(storeAmount));
    }
    if (storeAmount != null && !nextParams.get('amountComparator')) {
        nextParams.set('amountComparator', storeAmountComparator ?? TransactionAmountComparatorEnum.GT);
    }
    if (storeBankAccountId && !nextParams.get('bankAccountId')) {
        nextParams.set('bankAccountId', storeBankAccountId);
    }
    if (storeRelationIban && !nextParams.get('relationIban')) {
        nextParams.set('relationIban', storeRelationIban);
    }
    if (storeGeneralLedgerAccountId && !nextParams.get('generalLedgerAccountId')) {
        nextParams.set('generalLedgerAccountId', storeGeneralLedgerAccountId);
    }
    if (storeMatchStatus && storeMatchStatus !== TransactionMatchFilterEnum.ANY && !nextParams.get('matchStatus')) {
        nextParams.set('matchStatus', storeMatchStatus);
    }
    if (storeSearch && !nextParams.get('search')) {
        nextParams.set('search', storeSearch);
    }

    return nextParams;
}

export default function PartnerTransactionsPage({currentUser}: { currentUser: User }) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);
    const queryClient = useQueryClient();
    const {glAccounts, isLoading: glAccountsLoading} = useGLAccounts(currentUser);

    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);

    const {
        data: selectedMatch,
        isLoading: isMatchLoading,
        reload: reloadMatch,
    } = useMatchDetailsByMatchId(api, selectedMatchId);

    const documentDrawer = useDocumentDrawer();

    const invalidateTransactions = useCallback(
        () => queryClient.invalidateQueries({queryKey: [QueryKey.PartnerTransactions]}),
        [queryClient],
    );

    function handleOpenMatchedDocument(documentId: string) {
        if (!selectedMatch?.business_id) return;
        setSelectedMatchId(null);
        documentDrawer.open(selectedMatch.business_id, documentId);
    }

    async function handleUnmatchParts(billIds: string[], transactionIds: string[]) {
        if (!selectedMatchId) return;
        await api.Reconciliation__manuallyUnmatch({
            match_id: selectedMatchId,
            bill_ids: billIds,
            transaction_ids: transactionIds,
        });
        await reloadMatch();
        await invalidateTransactions();
    }

    async function handleUnmatchAll() {
        if (!selectedMatch) return;
        await handleUnmatchParts(
            selectedMatch.bill_parts.map((b) => b.id),
            selectedMatch.transaction_parts.map((t) => t.id),
        );
        setSelectedMatchId(null);
    }

    const [bankAccounts, setBankAccounts] = useState<Array<CustomerBankAccount>>([]);
    const clientScope = usePartnerClientScope(currentUser);

    const {state: filterStore, dispatch: filterDispatch} = useFilterStore();
    const [initialParams] = useState(() => withStoreDefaults(searchParams, filterStore));
    const isUrlHydratedRef = useRef(false);
    if (!isUrlHydratedRef.current && [...initialParams.entries()].every(([key, value]) => searchParams.get(key) === value)) {
        isUrlHydratedRef.current = true;
    }

    useEffect(() => {
        if (initialParams.toString() !== searchParams.toString()) {
            setSearchParams(initialParams, {replace: true});
        }
    }, []);

    const filtersKey = JSON.stringify(
        filtersFromParams(isUrlHydratedRef.current ? searchParams : initialParams)
    );
    const filters = useMemo<TransactionFilters>(() => JSON.parse(filtersKey), [filtersKey]);

    const sortParam = searchParams.get('sort');
    const directionParam = searchParams.get('direction');
    const sort = isSortField(sortParam) ? sortParam : DEFAULT_SORT;
    const direction = isSortDirection(directionParam) ? directionParam : DEFAULT_DIRECTION;

    const sortingState = useMemo<SortingState>(
        () => ({orderBy: SORT_FIELD_TO_COLUMN[sort], orderDescending: direction === 'desc'}),
        [sort, direction],
    );

    const setSortingState = useCallback((update: SortingState | ((previous: SortingState) => SortingState)) => {
        const next = typeof update === 'function' ? update(sortingState) : update;
        const nextSort = columnToSortField(next.orderBy);
        if (!nextSort) {
            return;
        }
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sort', nextSort);
        nextParams.set('direction', next.orderDescending ? 'desc' : 'asc');
        setSearchParams(nextParams);
    }, [sortingState, searchParams, setSearchParams]);

    const queryParams = useMemo(
        () => toPartnerTransactionsParams(filters, sort, direction),
        [filters, sort, direction],
    );

    const transactionsQuery = usePartnerTransactionsInfinite(api, queryParams);
    const pages = useMemo(() => transactionsQuery.data?.pages ?? [], [transactionsQuery.data]);
    const transactions = useMemo(
        () => pages.flatMap(page => page.items.map(mapPartnerTransaction)),
        [pages],
    );
    const total = pages[0]?.total ?? null;
    const tableTitle = total === null ? 'Transacties' : `${total} Transacties`;

    const {
        categorizationChangeById,
        pendingCategorizationChangeIds,
        resolvedTransactionIds,
    } = usePartnerTransactionPageEnrichment(api, pages);

    const {hasNextPage, isFetchNextPageError, isFetching, fetchNextPage} = transactionsQuery;
    const loadNextPage = useCallback(() => {
        if (hasNextPage && !isFetching) {
            void fetchNextPage({cancelRefetch: false});
        }
    }, [hasNextPage, isFetching, fetchNextPage]);

    const listRef = useRef<HTMLDivElement>(null);
    const queryParamsKey = JSON.stringify(queryParams);
    useEffect(() => {
        findScrollParent(listRef.current)?.scrollTo?.({top: 0});
    }, [queryParamsKey]);

    async function loadBankAccounts(clientId?: string) {
        const response = await api.Partner__managedBusinesses__bankAccounts(clientId ? {client_business_id: clientId} : {});
        const mapped = response.map((account) => CustomerBankAccount.fromApiResponse({
            ...account,
            is_expired: false,
            expires_soon: false,
        } as BankAccountData));
        setBankAccounts(mapped);
    }

    const {
        transactionToBook,
        isCategoryChangeLoading,
        openCategoryChange,
        closeCategoryChange,
        handleCategoryChangeSubmit,
    } = useTransactionCategoryChange({
        currentUser,
        defaultGlAccount: DEFAULT_GL_ACCOUNT,
        onCategoryChanged: invalidateTransactions,
    });

    const [transactionPendingUndo, setTransactionPendingUndo] = useState<Transaction | null>(null);

    async function handleUndoBookAway() {
        if (!transactionPendingUndo) {
            return;
        }
        await api.CategorizationEngine__undoBookAway({transaction_id: transactionPendingUndo.id});
        setTransactionPendingUndo(null);
        await invalidateTransactions();
    }

    const openCategoryChangeRef = useRef(openCategoryChange);
    openCategoryChangeRef.current = openCategoryChange;

    const onWrongGLAClick = useCallback((transaction: Transaction) => {
        openCategoryChangeRef.current({
            id: transaction.id,
            generalLedgerAccountId: transaction.generalLedgerAccount.id,
            relationName: transaction.relationName,
            relationIBAN: transaction.relationIBAN,
            subledgerId: transaction.generalLedgerAccount.subledger?.id ?? null,
        });
    }, []);

    const renderMatchAction = useCallback((transaction: Transaction) => {
        if (!transaction.reconciliationId) {
            return null;
        }

        return <MatchedStatusPill onClick={() => setSelectedMatchId(transaction.reconciliationId)} />;
    }, []);

    useEffect(() => {
        loadBankAccounts(filters.clientId);
    }, [filters.clientId]);

    const handleClientSelect = (clientId: string | null) => {
        filterDispatch({type: 'SET_FILTERS', payload: {transactions: {bankAccountId: null}}});
        clientScope.selectClient(clientId, {resetParams: ['bankAccountId']});
    };

    return (
        <SubledgerProvider user={currentUser} clientBusinessId={clientScope.selectedClientId}>
            <BookTransactionModal
                transactionToBook={transactionToBook}
                isOpen={!!transactionToBook}
                onClose={closeCategoryChange}
                onSubmit={handleCategoryChangeSubmit}
                isLoading={isCategoryChangeLoading}
                glAccounts={glAccounts}
                glAccountsLoading={glAccountsLoading}
            />

            <UndoBookAwayConfirmationModal
                transaction={transactionPendingUndo}
                isOpen={!!transactionPendingUndo}
                onClose={() => setTransactionPendingUndo(null)}
                onConfirm={handleUndoBookAway}
            />

            <Modal
                open={!!selectedMatchId}
                updateOpenState={(open) => {
                    if (!open) setSelectedMatchId(null);
                }}
                title="Match"
                width="4xl"
            >
                {isMatchLoading || !selectedMatch ? (
                    <div className="flex items-center justify-center h-48">
                        <p className="text-gray-500">Match laden...</p>
                    </div>
                ) : (
                    <MatchDetailsView
                        match={selectedMatch}
                        onUnmatchPart={handleUnmatchParts}
                        onUnmatchAll={handleUnmatchAll}
                        onOpenDocument={handleOpenMatchedDocument}
                    />
                )}
            </Modal>

            <Shell
                currentUser={currentUser}
                title="Transacties"
                subtitle={<ClientSubtitle prefix="Transacties van" clientName={clientScope.selectedClient?.name ?? null} fallback="Alle transacties van je klanten"/>}
                titleActions={
                    <PartnerClientToolbar
                        clients={clientScope.clients}
                        selectedClientId={clientScope.selectedClientId}
                        recentClientIds={clientScope.recentClientIds}
                        goToItemIds={getVisibleGoToItemIds((flag) => currentUser.flags.getFlag(flag))}
                        onSelectClient={handleClientSelect}
                    />
                }
            >

                <TransactionFilterBar
                    filters={filters}
                    filterOptions={{
                        bankAccounts: bankAccounts.map(ba => ({
                            id: ba.id,
                            name: `${ba.ownerName} (${ba.displayIdentifier()})`,
                            businessId: ba.businessId
                        })),
                        glAccounts: glAccounts
                    }}
                />

                <div className="flex flex-col" ref={listRef}>
                    {transactionsQuery.isError && !transactionsQuery.data ? <div className='w-full mt-24 text-center'>
                        <p className="text-gray-500">Transacties konden niet worden geladen</p>
                    </div> : <TransactionsTable
                        isLoading={transactionsQuery.isPending || glAccountsLoading}
                        transactions={transactions}
                        title={tableTitle}
                        emptyRowMessage="Geen transacties gevonden"
                        serverSortableFields={SORTABLE_COLUMNS}
                        footer={transactions.length > 0 ? (
                            <InfiniteScrollFooter
                                hasMore={Boolean(hasNextPage)}
                                isLoadingMore={isFetching}
                                hasLoadError={isFetchNextPageError}
                                onLoadMore={loadNextPage}
                                onRetry={loadNextPage}
                            />
                        ) : undefined}
                        bankAccounts={bankAccounts}
                        onWrongGLAClick={onWrongGLAClick}
                        onUndoBookAwayClick={setTransactionPendingUndo}
                        viewerRole={TransactionsTableViewerRole.PARTNER}
                        showsCategorizationChange
                        categorizationChangeById={categorizationChangeById}
                        pendingCategorizationChangeIds={pendingCategorizationChangeIds}
                        resolvedTransactionIds={resolvedTransactionIds}
                        sortingState={[sortingState, setSortingState]}
                        onQueryUpdate={ignoreQueryUpdate}
                        highlightText={filters.search}
                        renderRowActions={renderMatchAction}
                    />}
                </div>
            </Shell>
        </SubledgerProvider>
    );
}
